/**
 * Модальное окно (§2.3 «<Modal>», §13).
 *
 * Поведение, которое требует спека:
 *
 * • Фокус переносится внутрь и запирается там (focus trap), при закрытии
 *   возвращается на элемент-источник.
 * • Esc = «Отмена»; Enter запускает главное действие, но только если фокус не
 *   в поле ввода.
 * • Прокрутка фона блокируется, вложенные слои считаются счётчиком — поповер
 *   справочника внутри модалки не должен разблокировать фон.
 * • Для **необратимого** действия подложка и Esc не закрывают окно: выход
 *   только явной кнопкой (`dismissible={false}`).
 * • `role="dialog" aria-modal="true" aria-labelledby`.
 */
import { useCallback, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'
import { IconClose } from './icons'
import { isTopLayer, popLayer, pushLayer } from './overlay-stack'
import './Modal.css'

export interface ModalProps {
  open: boolean
  /** Закрытие = «Отмена». */
  onClose: () => void
  title: string
  children?: ReactNode
  /** Кнопки футера: главная — последняя. */
  footer?: ReactNode
  /** Подпись слева от кнопок: последствие необратимого действия (П6). */
  footerNote?: ReactNode
  /** s — 440 px, m — 560 px (по умолчанию). */
  size?: 's' | 'm' | 'l'
  /**
   * Можно ли закрыть окно Esc и кликом по подложке. Для необратимых действий —
   * `false`: выход только явной кнопкой.
   */
  dismissible?: boolean
  /** Главное действие: Enter вне поля ввода. */
  onConfirm?: () => void
}

/** Сколько модалок открыто: фон разблокируется только когда закрылась последняя. */
let openModals = 0

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  footerNote,
  size = 'm',
  dismissible = true,
  onConfirm,
}: ModalProps) {
  const reactId = useId()
  const titleId = `modal-title${reactId}`
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const layerIdRef = useRef<symbol | null>(null)

  const focusables = useCallback(() => {
    const dialog = dialogRef.current
    if (!dialog) return [] as HTMLElement[]
    return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (element) => element.offsetParent !== null || element === document.activeElement,
    )
  }, [])

  /* Слой в стеке: Esc адресован верхнему слою, а не всем сразу. */
  useEffect(() => {
    if (!open) {
      layerIdRef.current = null
      return
    }
    const id = pushLayer('modal')
    layerIdRef.current = id
    return () => {
      popLayer(id)
      layerIdRef.current = null
    }
  }, [open])

  /* Блокировка прокрутки фона. Счётчик — на случай модалки поверх модалки. */
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    openModals += 1
    document.body.style.overflow = 'hidden'
    return () => {
      openModals -= 1
      if (openModals === 0) document.body.style.overflow = previous
    }
  }, [open])

  /* Фокус внутрь при открытии и обратно на источник при закрытии (§13). */
  useEffect(() => {
    if (!open) return
    const source = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const first = focusables()[0]
    ;(first ?? dialogRef.current)?.focus()
    return () => source?.focus()
  }, [open, focusables])

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      const id = layerIdRef.current
      if (id == null || !isTopLayer(id)) return

      if (event.key === 'Escape' && dismissible) {
        event.preventDefault()
        onClose()
        return
      }

      /* Enter — главное действие, но не когда человек печатает (§2.3). */
      if (event.key === 'Enter' && onConfirm) {
        const target = event.target
        const typing =
          target instanceof HTMLElement &&
          (target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable ||
            target.tagName === 'BUTTON')
        if (!typing) {
          event.preventDefault()
          onConfirm()
        }
        return
      }

      /* Ловушка фокуса: Tab не выходит за пределы окна. */
      if (event.key === 'Tab') {
        const items = focusables()
        if (items.length === 0) {
          event.preventDefault()
          return
        }
        const first = items[0]
        const last = items[items.length - 1]
        const active = document.activeElement
        if (!event.shiftKey && active === last) {
          event.preventDefault()
          first?.focus()
        } else if (event.shiftKey && (active === first || active === dialogRef.current)) {
          event.preventDefault()
          last?.focus()
        }
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, dismissible, onClose, onConfirm, focusables])

  if (!open) return null

  return createPortal(
    <div
      className="modal-overlay"
      onMouseDown={(event) => {
        /* Клик именно по подложке, а не протяжка выделения изнутри окна. */
        if (dismissible && event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        className={`modal modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="modal__head">
          <h2 id={titleId} className="modal__title t-h2">
            {title}
          </h2>
          {/* Крестик — у окон, которые и так закрываются Esc и кликом по фону:
              явный выход должен быть виден, а не угадываться. У необратимых
              действий (dismissible={false}) выход только кнопкой в подвале. */}
          {dismissible ? (
            <button type="button" className="modal__close" aria-label="Закрыть" onClick={onClose}>
              <IconClose size={20} />
            </button>
          ) : null}
        </div>
        {children ? <div className="modal__body t-body">{children}</div> : null}
        {footer || footerNote ? (
          <div className="modal__footer">
            {footerNote ? <span className="modal__footer-note t-meta">{footerNote}</span> : null}
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
